// -------------------------------
// SHIFT SCHEDULE INTERFACES
// -------------------------------
export interface PendingShiftSchedule {
    employeeIds?: number[];
    departmentId?: number;
    shiftName?: string;
    shiftId?: string;

    fromDate?: string;
    toDate?: string;

    startTime?: string;
    endTime?: string;
    minStartTime?: string;
    maxStartTime?: string;
    minEndTime?: string;
    maxEndTime?: string;
    breakTime?: string;

    acceptExtraHours?: boolean;
    isPublished?: boolean;
}

export interface PendingDeleteShiftSchedule {
    awaitingEmployee?: boolean;
    awaitingDate?: boolean;
    employeeId?: number;
}


export const startCreateShiftScheduleFlow = (
    setPendingShift: (data: PendingShiftSchedule | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingShift({});

    pushAIChat("Sure! Which employee should I assign the shift to?");
    speak("Which employee should I assign the shift to?");

    // ✅ SHOW EMPLOYEE SUGGESTIONS
    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "employee" }
        })
    );
};

export const processPendingShiftSchedule = (
    text: string,
    pendingShift: PendingShiftSchedule,
    setPendingShift: (data: PendingShiftSchedule | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — EMPLOYEE
    if (!pendingShift.employeeIds) {
        const employeeId = Number(text);
        if (!employeeId) {
            pushAIChat("Please provide a valid employee.");
            speak("Please provide a valid employee.");
            return;
        }

        setPendingShift({ ...pendingShift, employeeIds: [employeeId] });
        pushAIChat("Which department should this shift be assigned to?");
        speak("Which department should this shift be assigned to?");
        return;
    }

    // STEP 2 — DEPARTMENT
    if (!pendingShift.departmentId) {
        const departmentId = Number(text);
        if (!departmentId) {
            pushAIChat("Please provide a valid department.");
            speak("Please provide a valid department.");
            return;
        }

        setPendingShift({ ...pendingShift, departmentId });
        pushAIChat("Which shift should I assign?");
        speak("Which shift should I assign?");
        return;
    }

    // STEP 3 — SHIFT NAME
    if (!pendingShift.shiftName) {
        const shiftName = text.trim();
        if (!shiftName) {
            pushAIChat("Please provide a valid shift name.");
            speak("Please provide a valid shift name.");
            return;
        }

        setPendingShift({ ...pendingShift, shiftName });
        pushAIChat("From which date should the shift start?");
        speak("From which date should the shift start?");
        return;
    }

    // STEP 4 — FROM DATE
    if (!pendingShift.fromDate) {
        setPendingShift({ ...pendingShift, fromDate: text });
        pushAIChat("Till which date should the shift apply?");
        speak("Till which date should the shift apply?");
        return;
    }

    // STEP 5 — TO DATE
    if (!pendingShift.toDate) {
        setPendingShift({ ...pendingShift, toDate: text });
        pushAIChat("What time does the shift start?");
        speak("What time does the shift start?");
        return;
    }

    // STEP 6 — START TIME
    if (!pendingShift.startTime) {
        setPendingShift({
            ...pendingShift,
            startTime: text,
            minStartTime: text,
            maxStartTime: text
        });

        pushAIChat("What time does the shift end?");
        speak("What time does the shift end?");
        return;
    }

    // STEP 7 — END TIME
    if (!pendingShift.endTime) {
        setPendingShift({
            ...pendingShift,
            endTime: text,
            minEndTime: text,
            maxEndTime: text
        });

        pushAIChat("How long is the break? (example: 00:30:00)");
        speak("How long is the break?");
        return;
    }

    // STEP 8 — BREAK TIME
    if (!pendingShift.breakTime) {
        setPendingShift({ ...pendingShift, breakTime: text });

        pushAIChat("Should extra hours be accepted? Yes or No?");
        speak("Should extra hours be accepted?");
        return;
    }

    // STEP 9 — EXTRA HOURS
    if (pendingShift.acceptExtraHours === undefined) {
        const acceptExtraHours = !text.toLowerCase().includes("no");

        setPendingShift({ ...pendingShift, acceptExtraHours });

        pushAIChat("Should this shift be published?");
        speak("Should this shift be published?");
        return;
    }

    // STEP 10 — PUBLISH (FINAL)
    if (pendingShift.isPublished === undefined) {
        const isPublished = !text.toLowerCase().includes("no");

        pushAIChat("Creating shift schedule...");
        speak("Creating shift schedule");

        window.dispatchEvent(
            new CustomEvent("ai-create-shiftschedule", {
                detail: {
                    employeeIds: pendingShift.employeeIds,
                    departmentId: pendingShift.departmentId,
                    shiftName: pendingShift.shiftName,
                    fromDate: pendingShift.fromDate,
                    toDate: pendingShift.toDate,
                    StartTime: pendingShift.startTime,
                    EndTime: pendingShift.endTime,
                    MinStartTime: pendingShift.minStartTime,
                    MaxStartTime: pendingShift.maxStartTime,
                    MinEndTime: pendingShift.minEndTime,
                    MaxEndTime: pendingShift.maxEndTime,
                    BreakTime: pendingShift.breakTime,
                    AcceptExtraHours: pendingShift.acceptExtraHours,
                    IsPublished: isPublished
                }
            })
        );

        setPendingShift(null);
    }
};

export const startDeleteShiftScheduleFlow = (
    setPendingDelete: (data: PendingDeleteShiftSchedule | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeIdFromIntent?: number
) => {
    if (employeeIdFromIntent) {
        setPendingDelete({
            awaitingDate: true,
            employeeId: employeeIdFromIntent
        });

        pushAIChat("Which date’s shift should I delete?");
        speak("Which date’s shift should I delete?");
        return;
    }

    setPendingDelete({ awaitingEmployee: true });
    pushAIChat("Which employee’s shift do you want to delete?");
    speak("Which employee’s shift do you want to delete?");
};

export const processPendingDeleteShiftSchedule = (
    text: string,
    pendingDelete: PendingDeleteShiftSchedule,
    setPendingDelete: (data: PendingDeleteShiftSchedule | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — EMPLOYEE
    if (pendingDelete.awaitingEmployee) {
        const employeeId = Number(text);

        if (!employeeId) {
            pushAIChat("Please provide a valid employee.");
            speak("Please provide a valid employee.");
            return;
        }

        setPendingDelete({
            awaitingDate: true,
            employeeId
        });

        pushAIChat("Which date’s shift should I delete?");
        speak("Which date’s shift should I delete?");
        return;
    }

    // STEP 2 — DATE
    if (pendingDelete.awaitingDate && pendingDelete.employeeId) {
        const date = text.trim();

        if (!date) {
            pushAIChat("Please provide a valid date.");
            speak("Please provide a valid date.");
            return;
        }

        pushAIChat("Deleting shift schedule...");
        speak("Deleting shift schedule");

        window.dispatchEvent(
            new CustomEvent("ai-delete-shiftschedule", {
                detail: {
                    employeeId: pendingDelete.employeeId,
                    date
                }
            })
        );

        setPendingDelete(null);
    }
};

